import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import {
  buildAssetUpsertOperation,
  buildInventoryItemUpsertOperation,
  buildInventoryMovementRecordOperation,
  type AssetStatus,
  type ConsumableItemType,
  type InventoryMovementType,
} from "../operations/warehouseAssetOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import {
  assetStatusLabel,
  consumableItemTypeLabel,
  inventoryMovementTypeLabel,
  loadWarehouseAssetSnapshot,
  type AssetRegisterItem,
  type ConsumableInventoryItem,
  type WarehouseAssetSnapshot,
} from "../warehouse/warehouseAssetManagement";
import { ConfirmationSheet } from "./ConfirmationSheet";

interface WarehouseAssetManagementPanelProps {
  farmId: string;
  userId: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

type PendingAction = "item" | "movement" | "asset";

const EMPTY_SNAPSHOT: WarehouseAssetSnapshot = {
  items: [],
  movements: [],
  assets: [],
  assetEvents: [],
};

function firstRecord(value: unknown): Record<string, unknown> | undefined {
  if (Array.isArray(value)) {
    const first = value[0];
    return first && typeof first === "object" ? (first as Record<string, unknown>) : undefined;
  }
  return value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
}

function localDateValue(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function numberInput(value: string): number {
  const normalized = value.replace(/[٬,\s]/g, "");
  return Number(normalized);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("fa-IR", { maximumFractionDigits: 4 }).format(value);
}

function formatToman(value: number): string {
  return `${new Intl.NumberFormat("fa-IR").format(value)} تومان`;
}

function syncMessage(status: string): string {
  if (status === "synced") return "تغییر با سرور همگام شد.";
  if (status === "pending") return "تغییر در صف آفلاین ذخیره شد.";
  if (status === "retryable_error") return "تغییر حفظ شد و دوباره ارسال می‌شود.";
  if (status === "terminal_conflict")
    return "ثبت با وضعیت فعلی تعارض دارد؛ اطلاعات را تازه‌سازی کنید.";
  return "تغییر ثبت شد.";
}

export function WarehouseAssetManagementPanel({
  farmId,
  userId,
  onSyncSummary,
}: WarehouseAssetManagementPanelProps) {
  const [snapshot, setSnapshot] = useState<WarehouseAssetSnapshot>(EMPTY_SNAPSHOT);
  const [canWriteInventory, setCanWriteInventory] = useState(false);
  const [canWriteAssets, setCanWriteAssets] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState<OfflineOperation>();
  const [pendingAction, setPendingAction] = useState<PendingAction>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);

  const [editingItem, setEditingItem] = useState<ConsumableInventoryItem>();
  const [itemCode, setItemCode] = useState("");
  const [itemType, setItemType] = useState<ConsumableItemType>("feed");
  const [itemName, setItemName] = useState("");
  const [baseUnit, setBaseUnit] = useState("g");
  const [reorderLevel, setReorderLevel] = useState("0");
  const [itemActive, setItemActive] = useState(true);

  const [movementItemId, setMovementItemId] = useState("");
  const [movementType, setMovementType] = useState<InventoryMovementType>("purchase");
  const [movementQuantity, setMovementQuantity] = useState("");
  const [unitCostToman, setUnitCostToman] = useState("");
  const [movementDate, setMovementDate] = useState(localDateValue);
  const [movementNotes, setMovementNotes] = useState("");

  const [editingAsset, setEditingAsset] = useState<AssetRegisterItem>();
  const [assetCode, setAssetCode] = useState("");
  const [assetName, setAssetName] = useState("");
  const [assetCategory, setAssetCategory] = useState("equipment");
  const [assetQuantity, setAssetQuantity] = useState("1");
  const [assetLocation, setAssetLocation] = useState("");
  const [assetStatus, setAssetStatus] = useState<AssetStatus>("active");
  const [assetPurchaseValue, setAssetPurchaseValue] = useState("");
  const [assetNote, setAssetNote] = useState("");
  const [assetDate, setAssetDate] = useState(localDateValue);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const [inventoryPermission, assetPermission, nextSnapshot] = await Promise.all([
        supabase.rpc("permission_decide", {
          target_farm_id: farmId,
          requested_permission: "inventory.write",
        }),
        supabase.rpc("permission_decide", {
          target_farm_id: farmId,
          requested_permission: "finance.write",
        }),
        loadWarehouseAssetSnapshot(supabase, farmId),
      ]);

      if (inventoryPermission.error) throw inventoryPermission.error;
      if (assetPermission.error) throw assetPermission.error;

      setCanWriteInventory(firstRecord(inventoryPermission.data)?.allowed === true);
      setCanWriteAssets(firstRecord(assetPermission.data)?.allowed === true);
      setSnapshot(nextSnapshot);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری انبار و دارایی‌ها انجام نشد.");
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeItems = useMemo(
    () => snapshot.items.filter((item) => item.isActive),
    [snapshot.items],
  );
  const lowStockCount = useMemo(
    () => activeItems.filter((item) => item.lowStock).length,
    [activeItems],
  );

  function operationContext() {
    return {
      actorId: userId,
      farmId,
      deviceId: getOrCreateDeviceId(),
      performedAt: new Date().toISOString(),
    };
  }

  function resetItemForm() {
    setEditingItem(undefined);
    setItemCode("");
    setItemType("feed");
    setItemName("");
    setBaseUnit("g");
    setReorderLevel("0");
    setItemActive(true);
  }

  function editItem(item: ConsumableInventoryItem) {
    setEditingItem(item);
    setItemCode(item.itemCode);
    setItemType(item.itemType);
    setItemName(item.displayName);
    setBaseUnit(item.baseUnit);
    setReorderLevel(String(item.reorderLevel));
    setItemActive(item.isActive);
    setMessage(undefined);
    setError(undefined);
  }

  function prepareItem() {
    try {
      setMessage(undefined);
      setError(undefined);
      if (!canWriteInventory) throw new Error("INVENTORY_WRITE_PERMISSION_DENIED");

      const level = numberInput(reorderLevel);
      if (!Number.isFinite(level)) throw new Error("INVENTORY_ITEM_INPUT_INVALID");

      const operation = buildInventoryItemUpsertOperation(operationContext(), {
        itemId: editingItem?.id ?? crypto.randomUUID(),
        expectedVersion: editingItem?.domainVersion ?? 0,
        itemCode,
        itemType,
        displayName: itemName,
        baseUnit,
        reorderLevel: level,
        isActive: itemActive,
      });

      setPending(operation);
      setPendingAction("item");
      setConfirmationParts([
        editingItem ? "ویرایش قلم انبار" : "قلم جدید انبار",
        `کد: ${operation.payload.itemCode as string}`,
        `نام: ${operation.payload.displayName as string}`,
        `نوع: ${consumableItemTypeLabel(itemType)}`,
        `واحد: ${operation.payload.baseUnit as string}`,
        `حد سفارش: ${formatNumber(level)}`,
        `وضعیت: ${itemActive ? "فعال" : "غیرفعال"}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات قلم انبار معتبر نیست.");
    }
  }

  function prepareMovement() {
    try {
      setMessage(undefined);
      setError(undefined);
      if (!canWriteInventory) throw new Error("INVENTORY_WRITE_PERMISSION_DENIED");
      if (!movementItemId) throw new Error("INVENTORY_ITEM_REQUIRED");

      const entered = numberInput(movementQuantity);
      if (!Number.isFinite(entered) || entered === 0) {
        throw new Error("INVENTORY_MOVEMENT_INPUT_INVALID");
      }

      const quantityDelta =
        movementType === "purchase"
          ? Math.abs(entered)
          : movementType === "usage" || movementType === "waste"
            ? -Math.abs(entered)
            : entered;
      const unitCost = unitCostToman.trim() ? numberInput(unitCostToman) : undefined;
      if (unitCost !== undefined && !Number.isFinite(unitCost)) {
        throw new Error("INVENTORY_MOVEMENT_INPUT_INVALID");
      }

      const operation = buildInventoryMovementRecordOperation(operationContext(), {
        movementId: crypto.randomUUID(),
        itemId: movementItemId,
        movementType,
        quantityDelta,
        ...(unitCost === undefined ? {} : { unitCostToman: unitCost }),
        occurredOn: movementDate,
        ...(movementNotes.trim() ? { notes: movementNotes } : {}),
      });

      const item = snapshot.items.find((candidate) => candidate.id === movementItemId);
      setPending(operation);
      setPendingAction("movement");
      setConfirmationParts([
        inventoryMovementTypeLabel(movementType),
        `قلم: ${item?.displayName ?? movementItemId}`,
        `تغییر موجودی: ${formatNumber(quantityDelta)} ${item?.baseUnit ?? ""}`,
        ...(unitCost === undefined ? [] : [`بهای واحد: ${formatToman(unitCost)}`]),
        `تاریخ: ${movementDate}`,
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات گردش انبار معتبر نیست.");
    }
  }

  function resetMovementForm() {
    setMovementItemId("");
    setMovementType("purchase");
    setMovementQuantity("");
    setUnitCostToman("");
    setMovementDate(localDateValue());
    setMovementNotes("");
  }

  function resetAssetForm() {
    setEditingAsset(undefined);
    setAssetCode("");
    setAssetName("");
    setAssetCategory("equipment");
    setAssetQuantity("1");
    setAssetLocation("");
    setAssetStatus("active");
    setAssetPurchaseValue("");
    setAssetNote("");
    setAssetDate(localDateValue());
  }

  function editAsset(asset: AssetRegisterItem) {
    setEditingAsset(asset);
    setAssetCode(asset.assetCode);
    setAssetName(asset.assetName);
    setAssetCategory(asset.category);
    setAssetQuantity(String(asset.quantity));
    setAssetLocation(asset.locationText ?? "");
    setAssetStatus(asset.status);
    setAssetPurchaseValue(
      asset.purchaseValueToman === undefined ? "" : String(asset.purchaseValueToman),
    );
    setAssetNote(asset.note ?? "");
    setAssetDate(localDateValue());
    setMessage(undefined);
    setError(undefined);
  }

  function prepareAsset() {
    try {
      setMessage(undefined);
      setError(undefined);
      if (!canWriteAssets) throw new Error("FINANCE_WRITE_PERMISSION_DENIED");

      const quantity = numberInput(assetQuantity);
      const purchaseValue = assetPurchaseValue.trim() ? numberInput(assetPurchaseValue) : undefined;
      if (
        !Number.isFinite(quantity) ||
        (purchaseValue !== undefined && !Number.isFinite(purchaseValue))
      ) {
        throw new Error("ASSET_INPUT_INVALID");
      }

      const operation = buildAssetUpsertOperation(operationContext(), {
        assetId: editingAsset?.id ?? crypto.randomUUID(),
        expectedVersion: editingAsset?.domainVersion ?? 0,
        assetCode,
        assetName,
        category: assetCategory,
        quantity,
        ...(assetLocation.trim() ? { locationText: assetLocation } : {}),
        status: assetStatus,
        ...(purchaseValue === undefined ? {} : { purchaseValueToman: purchaseValue }),
        ...(assetNote.trim() ? { note: assetNote } : {}),
        occurredOn: assetDate,
      });

      setPending(operation);
      setPendingAction("asset");
      setConfirmationParts([
        editingAsset ? "ویرایش دارایی" : "دارایی جدید",
        `کد: ${operation.payload.assetCode as string}`,
        `نام: ${operation.payload.assetName as string}`,
        `تعداد: ${formatNumber(quantity)}`,
        `وضعیت: ${assetStatusLabel(assetStatus)}`,
        ...(purchaseValue === undefined ? [] : [`ارزش خرید: ${formatToman(purchaseValue)}`]),
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات دارایی معتبر نیست.");
    }
  }

  async function confirmPending() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const action = pendingAction;
      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);
      setPendingAction(undefined);

      if (result.operation.status !== "terminal_conflict") {
        if (action === "item") resetItemForm();
        if (action === "movement") resetMovementForm();
        if (action === "asset") resetAssetForm();
      }

      if (result.operation.status === "synced") {
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت انبار یا دارایی انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  if (loading && snapshot.items.length === 0 && snapshot.assets.length === 0) {
    return (
      <section className="section-card">
        <p>در حال بارگذاری انبار و دارایی‌ها…</p>
      </section>
    );
  }

  return (
    <section className="section-card" aria-labelledby="warehouse-asset-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M6B</p>
          <h2 id="warehouse-asset-title">انبار مصرفی و دارایی‌ها</h2>
        </div>
        <button
          type="button"
          className="text-button"
          disabled={loading}
          onClick={() => void refresh()}
        >
          تازه‌سازی
        </button>
      </div>

      <p className="section-note">
        موجودی غذا، دارو و ملزومات از موجودی زیستی ماهی‌ها جداست. گردش انبار append-only است و
        دارایی‌ها حذف فیزیکی نمی‌شوند.
      </p>

      <div className="manager-grid">
        <article className="manager-card">
          <strong>اقلام فعال</strong>
          <p>{formatNumber(activeItems.length)}</p>
        </article>
        <article className="manager-card">
          <strong>نیازمند سفارش</strong>
          <p>{formatNumber(lowStockCount)}</p>
        </article>
        <article className="manager-card">
          <strong>دارایی‌ها</strong>
          <p>{formatNumber(snapshot.assets.length)}</p>
        </article>
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">انبار مصرفی</p>
            <h3>{editingItem ? "ویرایش قلم" : "قلم جدید"}</h3>
          </div>
          {editingItem ? (
            <button type="button" className="text-button" onClick={resetItemForm}>
              جدید
            </button>
          ) : null}
        </div>

        <div className="operation-field-grid">
          <label>
            کد قلم
            <input
              value={itemCode}
              disabled={Boolean(editingItem)}
              onChange={(event) => setItemCode(event.target.value)}
              placeholder="FI001"
            />
          </label>
          <label>
            نام
            <input
              value={itemName}
              maxLength={160}
              onChange={(event) => setItemName(event.target.value)}
            />
          </label>
          <label>
            نوع
            <select
              value={itemType}
              onChange={(event) => setItemType(event.target.value as ConsumableItemType)}
            >
              <option value="feed">غذا</option>
              <option value="medicine">دارو</option>
              <option value="supply">ملزومات</option>
              <option value="other">سایر</option>
            </select>
          </label>
          <label>
            واحد پایه
            <input
              value={baseUnit}
              maxLength={40}
              onChange={(event) => setBaseUnit(event.target.value)}
            />
          </label>
          <label>
            حد سفارش
            <input
              inputMode="decimal"
              value={reorderLevel}
              onChange={(event) => setReorderLevel(event.target.value)}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={itemActive}
              onChange={(event) => setItemActive(event.target.checked)}
            />
            قلم فعال است
          </label>
        </div>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !canWriteInventory}
          onClick={prepareItem}
        >
          بررسی و ثبت قلم
        </button>

        <div className="work-list">
          {snapshot.items.map((item) => (
            <div className="work-item pending" key={item.id}>
              <span className="work-main">
                <strong>
                  {item.itemCode} · {item.displayName}
                </strong>
                <span>
                  {consumableItemTypeLabel(item.itemType)} · موجودی {formatNumber(item.onHand)}{" "}
                  {item.baseUnit} · حد سفارش {formatNumber(item.reorderLevel)}
                  {item.lowStock ? " · نیازمند سفارش" : ""}
                </span>
              </span>
              <button type="button" className="text-button" onClick={() => editItem(item)}>
                ویرایش
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">گردش انبار</p>
            <h3>ورود / مصرف / اصلاح</h3>
          </div>
        </div>

        <div className="operation-field-grid">
          <label>
            قلم
            <select
              value={movementItemId}
              onChange={(event) => setMovementItemId(event.target.value)}
            >
              <option value="">انتخاب کنید</option>
              {activeItems.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.displayName} · {formatNumber(item.onHand)} {item.baseUnit}
                </option>
              ))}
            </select>
          </label>
          <label>
            نوع گردش
            <select
              value={movementType}
              onChange={(event) => setMovementType(event.target.value as InventoryMovementType)}
            >
              <option value="purchase">ورود / خرید</option>
              <option value="usage">مصرف</option>
              <option value="waste">ضایعات</option>
              <option value="adjustment">اصلاح موجودی</option>
            </select>
          </label>
          <label>
            مقدار
            <input
              inputMode="decimal"
              value={movementQuantity}
              onChange={(event) => setMovementQuantity(event.target.value)}
              placeholder={movementType === "adjustment" ? "+/- مقدار" : "مقدار مثبت"}
            />
          </label>
          <label>
            بهای واحد (تومان)
            <input
              inputMode="numeric"
              value={unitCostToman}
              onChange={(event) => setUnitCostToman(event.target.value)}
              placeholder="اختیاری"
            />
          </label>
          <label>
            تاریخ
            <input
              type="date"
              value={movementDate}
              onChange={(event) => setMovementDate(event.target.value)}
            />
          </label>
          <label>
            توضیح
            <input
              value={movementNotes}
              maxLength={1000}
              onChange={(event) => setMovementNotes(event.target.value)}
            />
          </label>
        </div>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !canWriteInventory}
          onClick={prepareMovement}
        >
          بررسی و ثبت گردش
        </button>

        <div className="work-list">
          {snapshot.movements.slice(0, 20).map((movement) => {
            const item = snapshot.items.find((candidate) => candidate.id === movement.itemId);
            return (
              <div className="work-item pending" key={movement.id}>
                <span className="work-main">
                  <strong>
                    {inventoryMovementTypeLabel(movement.movementType)} ·{" "}
                    {item?.displayName ?? movement.itemId}
                  </strong>
                  <span>
                    {formatNumber(movement.quantityDelta)} {item?.baseUnit ?? ""} ·{" "}
                    {movement.occurredOn}
                    {movement.unitCostToman === undefined
                      ? ""
                      : ` · ${formatToman(movement.unitCostToman)} / واحد`}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="manager-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">دارایی / تجهیزات</p>
            <h3>{editingAsset ? "ویرایش دارایی" : "دارایی جدید"}</h3>
          </div>
          {editingAsset ? (
            <button type="button" className="text-button" onClick={resetAssetForm}>
              جدید
            </button>
          ) : null}
        </div>

        <div className="operation-field-grid">
          <label>
            کد دارایی
            <input
              value={assetCode}
              disabled={Boolean(editingAsset)}
              onChange={(event) => setAssetCode(event.target.value)}
              placeholder="A0001"
            />
          </label>
          <label>
            نام
            <input
              value={assetName}
              maxLength={180}
              onChange={(event) => setAssetName(event.target.value)}
            />
          </label>
          <label>
            دسته
            <input
              value={assetCategory}
              maxLength={80}
              onChange={(event) => setAssetCategory(event.target.value)}
            />
          </label>
          <label>
            تعداد
            <input
              inputMode="decimal"
              value={assetQuantity}
              onChange={(event) => setAssetQuantity(event.target.value)}
            />
          </label>
          <label>
            محل
            <input
              value={assetLocation}
              maxLength={240}
              onChange={(event) => setAssetLocation(event.target.value)}
            />
          </label>
          <label>
            وضعیت
            <select
              value={assetStatus}
              onChange={(event) => setAssetStatus(event.target.value as AssetStatus)}
            >
              <option value="active">فعال</option>
              <option value="maintenance">تعمیر / سرویس</option>
              <option value="inactive">غیرفعال</option>
              <option value="retired">از رده خارج</option>
            </select>
          </label>
          <label>
            ارزش خرید (تومان)
            <input
              inputMode="numeric"
              value={assetPurchaseValue}
              onChange={(event) => setAssetPurchaseValue(event.target.value)}
              placeholder="اختیاری"
            />
          </label>
          <label>
            تاریخ رویداد
            <input
              type="date"
              value={assetDate}
              onChange={(event) => setAssetDate(event.target.value)}
            />
          </label>
          <label>
            توضیح
            <input
              value={assetNote}
              maxLength={1000}
              onChange={(event) => setAssetNote(event.target.value)}
            />
          </label>
        </div>

        <button
          type="button"
          className="secondary-button"
          disabled={busy || !canWriteAssets}
          onClick={prepareAsset}
        >
          بررسی و ثبت دارایی
        </button>

        <div className="work-list">
          {snapshot.assets.map((asset) => (
            <div className="work-item pending" key={asset.id}>
              <span className="work-main">
                <strong>
                  {asset.assetCode} · {asset.assetName}
                </strong>
                <span>
                  {assetStatusLabel(asset.status)} · تعداد {formatNumber(asset.quantity)}
                  {asset.locationText ? ` · ${asset.locationText}` : ""}
                  {asset.purchaseValueToman === undefined
                    ? ""
                    : ` · ${formatToman(asset.purchaseValueToman)}`}
                </span>
              </span>
              <button type="button" className="text-button" onClick={() => editAsset(asset)}>
                ویرایش
              </button>
            </div>
          ))}
        </div>
      </div>

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید انبار / دارایی"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => {
            setPending(undefined);
            setPendingAction(undefined);
          }}
          onConfirm={() => void confirmPending()}
        />
      ) : null}
    </section>
  );
}
